import React, { useContext } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import moment, { Moment } from 'moment';
import * as am4charts from '@amcharts/amcharts4/charts';
import { get, groupBy } from 'lodash';

import { IDeviceUsage } from '../../../../../../services/core/devicePerformance';
import { IDeviceEventFilterState, deviceEventFilterGroupNames } from '../events/deviceEventsTypes';
import { DeviceEvent } from '../../../../../../services/core/eventsTypes';
import { EditEvents, EditEventsAction, IEventFilterGroups, useEditEventsDispatch } from '../events/edit-events';
import { getDeviceEventFilterGroups } from '../events/deviceEvents';
import { EventsShown } from '../events-shown';
import { DeviceInfoContext } from '../../../index';
import { chartNames, ChartName } from './lib/chartNames';
import { createYAxis } from '../../../../../../lib/usageChartYAxis';
import { useFeatureTogglesContext } from '../../../../../../context/featureToggles';
import { StackedColumnsDateChart, IProps as IChartProps } from '@components/chart/stackedColumnsDateChart';

import './charts.css';
import '../events/deviceEvents.css';

import { formatTooltipDay } from '@components/chart/tooltips';
import { drawXAxisLabelsFromDateRange } from '@components/chart/lib';
import { formatDurationData } from '../../../deviceFormat';

export interface IProps {
  data: IDeviceUsage[],
  dateRange: { from: number, to: number },
  events: DeviceEvent[],
  eventFilters: IDeviceEventFilterState,
  dispatchEventFilters: React.Dispatch<EditEventsAction<ChartName>>,
  setDate: (date: Moment | string) => void,
  onMount: (chart: am4charts.XYChart) => am4charts.XYChart
}

export const useChartData = (props: IProps): { eventFilterGroups: IEventFilterGroups, chartProps: Pick<IChartProps<any>, "title" | "data" | "series" | "onBarClick" | "drawXAxis" | "drawYAxis"> } => {
  const { t } = useTranslation('performance');

  const featureToggles = useFeatureTogglesContext();

  const { platformType } = useContext(DeviceInfoContext);

  const eventFilterGroups = getDeviceEventFilterGroups(platformType, featureToggles);

  const data = React.useMemo(() => {
    if (!props.data.length) {
      return [];
    }
    const eventsByDay = groupBy(props.events, (event) => moment.utc(event.local).startOf('day').valueOf());
    return props.data.map(({ date, used }) => {
      return {
        date: moment.utc(parseInt(date)).valueOf(),
        used: used || 0,
        events: get(eventsByDay, date, [])
      };
    });

  }, [props.data, props.events]);

  const series = [
    {
      colour: '#00aa55',
      dataKey: 'used',
      description: t('IN_USE', { ns: 'performance' }),
      visible: true
    }
  ];

  const onBarClick = (event: IDeviceUsage) => {
    props.setDate(moment.utc(event.date));
  };

  const title = t('DEVICE_UTILISATION_PAST_30_DAYS');

  return {
    eventFilterGroups,
    chartProps: {
      data,
      series,
      title,
      onBarClick,
      drawYAxis: (chart, yAxis) => {
        const maxUserActivitySeconds = Math.max(...chart.data.map((data) => data['used']));
        const maxUserActivityHours = maxUserActivitySeconds / 60 / 60;
        (yAxis as am4charts.DurationAxis).baseUnit = "second";

        createYAxis(yAxis as am4charts.DurationAxis, maxUserActivityHours, t, true);

        yAxis.renderer.labels.template.disabled = true;
      },
      drawXAxis: (_, xAxis) => drawXAxisLabelsFromDateRange(xAxis as am4charts.DateAxis, props.dateRange)
    }
  };
};

export function DeviceUsage30Days(props: IProps) {
  const { t } = useTranslation(['performance', 'editEvents', 'translation']);

  const { handleUpdate, handleApply, handleApplyAll, handleCancel } = useEditEventsDispatch(
    (props as IProps).dispatchEventFilters,
    chartNames.deviceUsage30Days,
    /* istanbul ignore next */
    () => {}
  );

  const { chartProps, eventFilterGroups } = useChartData(props);

  return (
    <div className={`core-device_performance-30-day-device-usage`}>

      <StackedColumnsDateChart<any>
        {...chartProps}
        headerControls={<div className="chart-edit-events">
          <EventsShown eventFilters={props.eventFilters.appliedEventFilters} />
          <EditEvents
            subheader={`‘${chartProps.title}’`}
            eventGroupNames={deviceEventFilterGroupNames}
            eventFilterGroups={eventFilterGroups}
            eventFilterState={props.eventFilters.currentEventFilters}
            handleUpdate={handleUpdate}
            handleApply={handleApply}
            handleCancel={handleCancel}
            handleApplyAll={handleApplyAll}
          />
        </div>}
        cypressId="last30DaysDeviceUsage"
        hasLegend={true}
        yAxisType='duration'
        xAxisType={'date'}
        dateAxisEndDuration={{ amount: "1", unit: 'day' }}
        formatTooltipDate={formatTooltipDay}
        useAxisTooltip
        formatData={formatDurationData(t, true)}
        eventOverlay={true}
        onMount={props.onMount}
        adjustChartDimensions={{ height: 250 }}
      />
    </div>
  );
}
